import React from 'react';
import { isPast, isToday } from 'date-fns';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Coin, { CoinColor } from 'components/Coin';
import Icon from 'components/Icon';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text, { TextColor } from 'components/Text';
import { abbreviatedMonthAndDayFormat } from 'lib/dateTimeFormat';
import TimezoneHelper from 'lib/timezones';
import { CoachingAvailabilitySchedule } from 'coaching/containers/CoachAvailabilityContainer';
import { MouseEventHandler } from 'types/typescript/eventHandlers';

import '../styles/CoachAvailabilitySchedulesList.scss';

export type ExactlyOneOrTwoSchedules = [
  CoachingAvailabilitySchedule,
  CoachingAvailabilitySchedule | null
];

export type Props = {
  onClickSchedule: (schedule: CoachingAvailabilitySchedule) => void;
  onClickSetUpcomingSchedule: () => void;
  schedules: ExactlyOneOrTwoSchedules;
  timezone: string;
};

export default function CoachAvailabilitySchedulesList(props: Props) {
  return (
    <Box inline noShadow textAlign="left" className="CoachAvailabilitySchedulesList">
      <BoxBody size="xsmall">
        <Text weight="semibold">
          Availability schedules ({TimezoneHelper.convertToReadableShortCode(props.timezone)})
        </Text>
      </BoxBody>
      <ScheduleRows {...props} />
    </Box>
  );
}

function ScheduleRows(props: Props) {
  const { schedules, onClickSchedule, onClickSetUpcomingSchedule } = props;

  // note: this makes the assumption that the schedules we get from the backend
  // are already in chronological order
  const firstSchedule = (
    <ScheduleRowFilled schedule={schedules[0]} onClickSchedule={onClickSchedule} />
  );
  const secondSchedule = schedules[1] ? (
    <ScheduleRowFilled schedule={schedules[1]} showStartDate onClickSchedule={onClickSchedule} />
  ) : (
    <ScheduleRowEmpty onClickSetUpcomingSchedule={onClickSetUpcomingSchedule} />
  );
  return (
    <>
      <Row size="xxsmall">{firstSchedule}</Row>
      <Row size="xsmall">{secondSchedule}</Row>
    </>
  );
}

function ScheduleRowEmpty({
  onClickSetUpcomingSchedule,
}: {
  onClickSetUpcomingSchedule: () => void;
}) {
  return (
    <ScheduleRow
      data-test="schedule-row-empty"
      coinColor="whiteout"
      coinBorderColor="graphite"
      labelColor="graphite"
      label="Set upcoming schedule"
      onClick={onClickSetUpcomingSchedule}
    />
  );
}

function ScheduleRowFilled({
  schedule,
  onClickSchedule,
  showStartDate,
}: {
  schedule: CoachingAvailabilitySchedule;
  onClickSchedule: (schedule: CoachingAvailabilitySchedule) => void;
  showStartDate?: boolean;
}) {
  function getSubtext() {
    if (schedule.endDate) {
      return `Ends on ${abbreviatedMonthAndDayFormat(schedule.endDate)}`;
    } else if (showStartDate) {
      return `Starts on ${abbreviatedMonthAndDayFormat(schedule.startDate)}`;
    } else {
      return undefined;
    }
  }

  const scheduleIsActive = isPast(schedule.startDate) || isToday(schedule.startDate);
  return (
    <ScheduleRow
      data-test={`schedule-row-${schedule.uuid}`}
      coinColor={scheduleIsActive ? 'atlas' : 'graphite'}
      labelColor="chalkboard"
      label={schedule.label}
      subtext={getSubtext()}
      onClick={() => onClickSchedule(schedule)}
    />
  );
}

function ScheduleRow({
  coinColor,
  coinBorderColor,
  labelColor,
  label,
  subtext,
  onClick,
}: {
  coinColor: CoinColor;
  coinBorderColor?: CoinColor;
  labelColor: TextColor;
  label: string;
  onClick: MouseEventHandler;
  subtext?: string;
}) {
  const labelComponent = subtext ? (
    <>
      <Text color={labelColor} truncate element="div">
        {label}
      </Text>
      <Text color="graphite" truncate lineHeight="flush" data-test={`subtext-${subtext}`}>
        {subtext}
      </Text>
    </>
  ) : (
    <Text color={labelColor} truncate lineHeight="flush">
      {label}
    </Text>
  );
  return (
    <a className="CoachAvailabilitySchedulesList-schedule" onClick={onClick}>
      <MediaObject figure="right" center>
        <MediaObject gutter="xsmall" center={!subtext}>
          <Coin color={coinColor} border={coinBorderColor} />
          {labelComponent}
        </MediaObject>
        <Icon name="pencil-stroke" className="CoachAvailabilitySchedulesList-schedule-editIcon" />
      </MediaObject>
    </a>
  );
}
